import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import { toast } from 'sonner';
import api from '../../../utils/api';
import { exportDataToCSV } from '../../../utils/exportUtils';
import { Loader, AlertCircle } from 'lucide-react';
import {
  Bar, BarChart, CartesianGrid, Cell, Line, LineChart, Pie, PieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis
} from 'recharts';

const CHART_COLORS = ['#1E3A5F', '#4A90A4', '#F39C12', '#27AE60', '#E74C3C', '#9B59B6'];

const formatCreatedAt = (value) => {
  const date = value?.toDate
    ? value.toDate()
    : new Date(value?._seconds !== undefined
      ? value._seconds * 1000
      : value?.seconds !== undefined
        ? value.seconds * 1000
        : value);
  return Number.isNaN(date.getTime()) ? 'Unknown' : date.toLocaleString();
};

const ReportPreviewModal = ({ isOpen, onClose, reportId }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && reportId) {
      fetchReport();
    }
  }, [isOpen, reportId]);

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      // Template: ABC Analysis (id: 1)
      if (reportId === 1) {
        const { data: products } = await api.get('/products');
        if (!products) throw new Error('No products found');

        const items = products.map(p => ({
          id: p.id,
          product_name: p.name || 'Unnamed Product',
          sku: p.sku || 'N/A',
          barcode: p.barcode || 'N/A',
          quantity: Number(p.quantity) || 0,
          value: (Number(p.price) || Number(p.cost) || 0) * (Number(p.quantity) || 0)
        }));

        const totalValue = items.reduce((sum, i) => sum + i.value, 0);
        const sorted = items.sort((a, b) => b.value - a.value);

        let cumulative = 0;
        const reportData = sorted.map(i => {
          cumulative += i.value;
          const percentage = totalValue > 0 ? (cumulative / totalValue) * 100 : 0;
          const classification = percentage <= 80 ? 'A' : percentage <= 95 ? 'B' : 'C';
          return { ...i, cumulative_percentage: Number(percentage.toFixed(2)), class: classification };
        });

        setReport({
          id: reportId,
          title: 'ABC Analysis',
          type: 'abc_analysis',
          createdAt: new Date(),
          data: reportData,
          filters: {}
        });
      }
      // Template: Slow-Moving Inventory (id: 2)
      else if (reportId === 2) {
        const [productsRes, checkoutsRes] = await Promise.all([
          api.get('/products'),
          api.get('/checkouts')
        ]);

        const products = productsRes.data || [];
        const checkouts = checkoutsRes.data || [];

        const soldMap = new Map();
        const lastSoldMap = new Map();
        checkouts.forEach(co => {
          const items = co.items || [];
          const coDate = co.createdAt?.seconds ? new Date(co.createdAt.seconds * 1000) : new Date(co.createdAt || Date.now());
          items.forEach(item => {
            const pid = item.productId || item.id;
            const qty = Number(item.quantity) || 0;
            soldMap.set(pid, (soldMap.get(pid) || 0) + qty);
            const prev = lastSoldMap.get(pid);
            if (!prev || coDate > prev) lastSoldMap.set(pid, coDate);
          });
        });

        const data = products.map(p => {
          const current = Number(p.quantity) || 0;
          const sold = soldMap.get(p.id) || 0;
          const turnover = current > 0 ? sold / current : 0;
          const last = lastSoldMap.get(p.id);
          return {
            product_name: p.name || 'Unnamed Product',
            sku: p.sku || 'N/A',
            barcode: p.barcode || 'N/A',
            current_stock: current,
            items_sold: sold,
            turnover_rate: Number(turnover.toFixed(2)),
            last_movement: last ? last.toLocaleDateString() : 'Never'
          };
        })
        // Show the slowest movers first
        .sort((a, b) => a.turnover_rate - b.turnover_rate);

        setReport({
          id: reportId,
          title: 'Slow-Moving Inventory',
          type: 'slow_moving',
          createdAt: new Date(),
          data,
          filters: {}
        });
      }
      // Template: Reorder Recommendations (id: 3)
      else if (reportId === 3) {
        const { data: products } = await api.get('/products');
        const data = (products || []).map(p => {
          const stock = Number(p.quantity) || 0;
          const rp = Number(p.reorderPoint) || 10;
          const recommended = Math.max(rp * 2 - stock, 0);
          return {
            product_name: p.name || 'Unnamed Product',
            barcode: p.barcode || 'N/A',
            current_stock: stock,
            reorder_point: rp,
            recommended_order: recommended
          };
        }).filter(r => r.recommended_order > 0)
          .sort((a, b) => b.recommended_order - a.recommended_order);

        setReport({
          id: reportId,
          title: 'Reorder Recommendations',
          type: 'reorder',
          createdAt: new Date(),
          data,
          filters: {}
        });
      }
      // Template: Forecasting Reports (id: 4)
      else if (reportId === 4) {
        const { data: products } = await api.get('/products');
        const data = (products || []).map(p => {
          const stock = Number(p.quantity) || 0;
          const rp = Number(p.reorderPoint) || 10;
          const recommended = Math.max(rp * 2 - stock, 0);
          return {
            product_name: p.name || 'Unnamed Product',
            barcode: p.barcode || 'N/A',
            current_stock: stock,
            reorder_point: rp,
            forecast_days_remaining: stock > 0 ? Math.floor(stock / (rp * 0.1)) : 0,
            recommended_order: recommended
          };
        }).filter(r => r.recommended_order > 0 || r.current_stock <= r.reorder_point)
          .sort((a, b) => b.recommended_order - a.recommended_order);

        setReport({
          id: reportId,
          title: 'Forecasting Reports',
          type: 'forecasting',
          createdAt: new Date(),
          data,
          filters: {}
        });
      }
      // For saved reports in DB, use the API
      else {
        try {
          const { data } = await api.get(`/reports/${reportId}`);
          setReport(data);
        } catch (apiError) {
          // If report not found in database, show a helpful message
          if (apiError.response?.status === 404) {
            setError('This report is not available. It may have been deleted or not yet generated.');
          } else {
            throw apiError; // Re-throw other errors
          }
        }
      }
    } catch (err) {
      console.error('Error fetching report:', err);
      setError('Failed to load report. Please try again.');
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (!report) return;
    
    try {
      const headers = Object.keys(report.data[0] || {});
      const rows = report.data.map(row => headers.map(header => row[header]));
      const result = exportDataToCSV(headers, rows, `${report.title || report.name || 'Report'}_${new Date().toISOString().split('T')[0]}`);
      if (result.success) toast.success('Report exported successfully');
      else toast.error('Failed to export report');
    } catch (err) {
      console.error('Error exporting report:', err);
      toast.error('Failed to export report');
    }
  };

  const renderReportContent = () => {
    if (!report) return null;
    
    // Handle case when report data is empty or undefined
    if (!report.data || !Array.isArray(report.data) || report.data.length === 0) {
      return (
        <div className="max-h-[60vh] overflow-auto">
          <h3 className="text-lg font-medium mb-4">{report.title || report.name || 'Report Preview'}</h3>
          
          {/* Report metadata */}
          <div className="mb-4 text-sm text-gray-500">
            <p>Generated: {formatCreatedAt(report.createdAt)}</p>
            <p>Type: {report.type}</p>
            {report.filters && Object.keys(report.filters).length > 0 && (
              <p>Filters: {Object.entries(report.filters)
                .filter(([_, value]) => value)
                .map(([key, value]) => `${key}: ${value}`)
                .join(', ')}</p>
            )}
          </div>
          
          <div className="p-8 text-center">
            <div className="mb-4">
              <AlertCircle size={48} className="mx-auto text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900">No data available</h3>
            <p className="mt-2 text-sm text-gray-500">
              There is no data available for this report. Please try adjusting your filters or generating a different report.
            </p>
          </div>
        </div>
      );
    }

    const fields = Object.keys(report.data[0]);
    const labelField = fields.find(field => typeof report.data[0][field] === 'string') || fields[0];
    const numericField = fields.find(field => typeof report.data[0][field] === 'number');
    const renderVisualization = () => {
      if (report.visualization === 'summary_cards') {
        const totals = fields.filter(field => report.data.some(row => typeof row[field] === 'number'));
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
            <div className="border rounded-md p-4">
              <p className="text-xs uppercase text-gray-500">Rows</p>
              <p className="text-xl font-semibold">{report.data.length}</p>
            </div>
            {totals.map(field => (
              <div key={field} className="border rounded-md p-4">
                <p className="text-xs uppercase text-gray-500">{field.replace(/_/g, ' ')}</p>
                <p className="text-xl font-semibold">
                  {report.data.reduce((sum, row) => sum + (Number(row[field]) || 0), 0).toLocaleString()}
                </p>
              </div>
            ))}
          </div>
        );
      }
      if (!numericField || !['bar_chart', 'line_chart', 'pie_chart'].includes(report.visualization)) return null;
      return (
        <div className="h-72 mb-4">
          <ResponsiveContainer width="100%" height="100%">
            {report.visualization === 'pie_chart' ? (
              <PieChart>
                <Pie data={report.data} dataKey={numericField} nameKey={labelField} outerRadius={100} label>
                  {report.data.map((row, index) => <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            ) : report.visualization === 'line_chart' ? (
              <LineChart data={report.data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey={labelField} />
                <YAxis />
                <Tooltip />
                <Line dataKey={numericField} stroke="#4A90A4" />
              </LineChart>
            ) : (
              <BarChart data={report.data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey={labelField} />
                <YAxis />
                <Tooltip />
                <Bar dataKey={numericField} fill="#4A90A4" />
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>
      );
    };

    return (
      <div className="max-h-[60vh] overflow-auto">
        <h3 className="text-lg font-medium mb-4">{report.title || report.name || 'Report Preview'}</h3>
        
        {/* Report metadata */}
        <div className="mb-4 text-sm text-gray-500">
          <p>Generated: {formatCreatedAt(report.createdAt)}</p>
          <p>Type: {report.type}</p>
          {report.filters && Object.keys(report.filters).length > 0 && (
            <p>Filters: {Object.entries(report.filters)
              .filter(([_, value]) => value)
              .map(([key, value]) => `${key}: ${value}`)
              .join(', ')}</p>
          )}
        </div>
        
        {renderVisualization()}

        {/* Report data table */}
        <div className="border rounded-md overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {report.data.length > 0 && Object.keys(report.data[0]).map((key) => (
                  <th
                    key={key}
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {key.replace(/_/g, ' ')}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {report.data.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {Object.entries(row).map(([key, value], cellIndex) => (
                    <td key={`${rowIndex}-${cellIndex}`} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {value !== null && value !== undefined ? String(value) : 'N/A'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl">
        <div className="p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader className="h-8 w-8 animate-spin text-primary mb-4" />
              <p className="text-gray-500">Loading report...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12">
              <AlertCircle className="h-8 w-8 text-red-500 mb-4" />
              <p className="text-gray-900 font-medium mb-2">Something went wrong</p>
              <p className="text-gray-500">{error}</p>
            </div>
          ) : (
            renderReportContent()
          )}
        </div>
        <div className="px-6 py-4 bg-gray-50 rounded-b-lg flex justify-end space-x-3">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          {!loading && !error && report && (
            <Button variant="default" onClick={handleExport}>
              Export
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportPreviewModal;

//     return (
//       <div className="max-h-[60vh] overflow-auto">
//         <h3 className="text-lg font-medium mb-4">{report.title || 'Report Preview'}</h3>
        
//         {/* Report metadata */}
//         <div className="mb-4 text-sm text-gray-500">
//           <p>Generated: {new Date(report.createdAt).toLocaleString()}</p>
//           <p>Type: {report.type}</p>
//           {report.filters && Object.keys(report.filters).length > 0 && (
//             <p>Filters: {Object.entries(report.filters)
//               .filter(([_, value]) => value)
//               .map(([key, value]) => `${key}: ${value}`)
//               .join(', ')}</p>
//           )}
//         </div>
        
//         {/* Report data table */}
//         <div className="border rounded-md overflow-hidden">
//           <table className="min-w-full divide-y divide-gray-200">
//             <thead className="bg-gray-50">
//               <tr>
//                 {Object.keys(report.data[0]).map((header) => (
//                   <th 
//                     key={header}
//                     className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
//                   >
//                     {header.replace(/_/g, ' ')}
//                   </th>
//                 ))}
//               </tr>
//             </thead>
//             <tbody className="bg-white divide-y divide-gray-200">
//               {report.data.map((row, rowIndex) => (
//                 <tr key={rowIndex}>
//                   {Object.values(row).map((cell, cellIndex) => (
//                     <td 
//                       key={cellIndex}
//                       className="px-6 py-4 whitespace-nowrap text-sm text-gray-500"
//                     >
//                       {cell === null || cell === undefined ? 'N/A' : 
//                        typeof cell === 'object' ? JSON.stringify(cell) : cell}
//                     </td>
//                   ))}
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         </div>
//       </div>
//     );
//   };

//   if (!isOpen) return null;

//   return (
//     <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
//       <div className="bg-card border border-border rounded-lg w-full max-w-3xl">
//         <div className="flex justify-between items-center p-6 border-b border-border">
//           <h2 className="text-xl font-semibold">Report Preview</h2>
//           <div className="flex space-x-2">
//             <Button variant="outline" onClick={onClose}>Close</Button>
//             <Button onClick={handleExport} disabled={loading || !report} iconName="Download" iconPosition="left">
//               Export CSV
//             </Button>
//           </div>
//         </div>
//         <div className="p-6">
//           {loading ? (
//             <div className="flex justify-center items-center h-64">
//               <Loader className="animate-spin h-8 w-8 text-primary" />
//               <span className="ml-2">Loading report...</span>
//             </div>
//           ) : error ? (
//             <div className="text-center text-red-500 p-4">{error}</div>
//           ) : report ? (
//             renderReportContent()
//           ) : (
//             <div className="text-center text-gray-500 p-4">No report data available</div>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default ReportPreviewModal;